// Enhanced deviceSettings.ts - Cross-browser compatible version
import { DeviceState } from '../hooks/useSmartDeviceDetection/useSmartDeviceDetection';

// Enhanced browser detection with better accuracy
export const getBrowser = (): string => {
  const userAgent = navigator.userAgent;
  const vendor = navigator.vendor || '';

  // Check for specific browsers (order matters - most specific first)
  if (userAgent.includes('Edg/')) return 'Edge'; // New Edge (Chromium-based)
  if (userAgent.includes('EdgA/')) return 'Edge'; // Edge on Android
  if (userAgent.includes('EdgiOS/')) return 'Edge'; // Edge on iOS
  if (userAgent.includes('OPR/') || userAgent.includes('Opera/')) return 'Opera';
  if (userAgent.includes('brave') || userAgent.includes('Brave')) return 'Brave';
  if (userAgent.includes('Vivaldi')) return 'Vivaldi';
  if (userAgent.includes('YaBrowser')) return 'Yandex';
  if (userAgent.includes('Firefox/')) return 'Firefox';
  if (userAgent.includes('Chrome/') && vendor.includes('Google')) return 'Chrome';
  if (userAgent.includes('Safari/') && vendor.includes('Apple') && !userAgent.includes('Chrome')) return 'Safari';
  if (userAgent.includes('MSIE') || userAgent.includes('Trident/')) return 'Internet Explorer';

  return 'Unknown';
};

// Platform detection with better accuracy
const getPlatformInfo = () => {
  const platform = navigator.platform || '';
  const userAgent = navigator.userAgent;

  const isWindows = platform.includes('Win') || userAgent.includes('Windows');
  const isMac = platform.includes('Mac') || userAgent.includes('Macintosh');
  const isLinux = platform.includes('Linux') && !userAgent.includes('Android');
  const isAndroid = userAgent.includes('Android');
  const isiOS = /iPad|iPhone|iPod/.test(userAgent) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  return { isWindows, isMac, isLinux, isAndroid, isiOS };
};

// Main entry point - Enhanced cross-platform settings opener
export const openWindowsSettingsForError = (deviceType: 'camera' | 'microphone', errorState: DeviceState) => {
  const { isWindows, isMac, isLinux, isAndroid, isiOS } = getPlatformInfo();
  const browser = getBrowser();

  console.log(`🔧 Opening settings for ${deviceType} error: ${errorState}`, {
    platform: { isWindows, isMac, isLinux, isAndroid, isiOS },
    browser,
    userAgent: navigator.userAgent,
  });

  // Mobile platforms
  if (isAndroid || isiOS) {
    showMobileInstructions(deviceType, errorState, isAndroid ? 'Android' : 'iOS');
    return;
  }

  // Desktop platforms
  if (isWindows) {
    handleWindowsSettings(deviceType, errorState, browser);
  } else if (isMac) {
    handleMacSettings(deviceType, errorState, browser);
  } else if (isLinux) {
    handleLinuxSettings(deviceType, errorState, browser);
  } else {
    // Unknown platform - show generic instructions
    showCrossPlatformInstructions(deviceType, errorState);
  }
};

// Windows settings handler with browser-specific fallbacks
const handleWindowsSettings = (deviceType: 'camera' | 'microphone', errorState: DeviceState, browser: string) => {
  let settingsUri = '';

  // Route to specific settings page based on error type
  switch (errorState) {
    case DeviceState.PERMISSION_DENIED:
      settingsUri = deviceType === 'camera' ? 'ms-settings:privacy-webcam' : 'ms-settings:privacy-microphone';
      break;

    case DeviceState.SYSTEM_BLOCKED:
      settingsUri = deviceType === 'camera' ? 'ms-settings:privacy-webcam' : 'ms-settings:sound-devices';
      break;

    case DeviceState.DEVICE_UNPLUGGED:
    case DeviceState.NO_DEVICE:
      settingsUri = deviceType === 'camera' ? 'ms-settings:connecteddevices' : 'ms-settings:sound';
      break;

    case DeviceState.DEVICE_IN_USE:
      settingsUri = deviceType === 'camera' ? 'ms-settings:privacy-webcam' : 'ms-settings:privacy-microphone';
      break;

    default:
      settingsUri = deviceType === 'camera' ? 'ms-settings:privacy-webcam' : 'ms-settings:sound-devices';
      break;
  }

  try {
    console.log(`🪟 Opening Windows settings: ${settingsUri} for error: ${errorState}`);

    // Try to open Windows settings
    const opened = window.open(settingsUri, '_blank');

    // If popup was blocked or URI not supported, show instructions
    setTimeout(() => {
      if (!opened || opened.closed) {
        console.log('Windows settings URI blocked or unsupported, showing instructions');
        showSystemInstructionsForError('Windows', deviceType, errorState, browser);
      }
    }, 1000);
  } catch (error) {
    console.log('❌ Failed to open Windows settings, showing instructions');
    showSystemInstructionsForError('Windows', deviceType, errorState, browser);
  }
};

// Mac settings handler with improved fallbacks
const handleMacSettings = (deviceType: 'camera' | 'microphone', errorState: DeviceState, browser: string) => {
  try {
    let macUri = '';

    switch (errorState) {
      case DeviceState.SYSTEM_BLOCKED:
        macUri =
          deviceType === 'camera'
            ? 'x-apple.systempreferences:com.apple.preference.security?Privacy_Camera'
            : 'x-apple.systempreferences:com.apple.preference.sound';
        break;

      case DeviceState.PERMISSION_DENIED:
      case DeviceState.DEVICE_IN_USE:
        macUri =
          deviceType === 'camera'
            ? 'x-apple.systempreferences:com.apple.preference.security?Privacy_Camera'
            : 'x-apple.systempreferences:com.apple.preference.security?Privacy_Microphone';
        break;

      default:
        macUri = 'x-apple.systempreferences:com.apple.preference.security';
    }

    console.log(`🍎 Trying Mac settings: ${macUri}`);

    // Try to open System Preferences
    const opened = window.open(macUri, '_blank');

    // Most browsers block this, so show instructions after a brief delay
    setTimeout(() => {
      showSystemInstructionsForError('Mac', deviceType, errorState, browser);
    }, 500);
  } catch (error) {
    console.log('❌ Mac settings URI failed, showing instructions');
    showSystemInstructionsForError('Mac', deviceType, errorState, browser);
  }
};

// Linux settings handler
const handleLinuxSettings = (deviceType: 'camera' | 'microphone', errorState: DeviceState, browser: string) => {
  console.log(`🐧 Linux platform detected, showing instructions for ${deviceType} error: ${errorState}`);
  showSystemInstructionsForError('Linux', deviceType, errorState, browser);
};

// Mobile instructions
const showMobileInstructions = (
  deviceType: 'camera' | 'microphone',
  errorState: DeviceState,
  platform: 'Android' | 'iOS'
) => {
  const device = deviceType === 'camera' ? 'Camera' : 'Microphone';

  let instructions = `${platform} ${device} Settings:\n\n`;

  if (platform === 'Android') {
    switch (errorState) {
      case DeviceState.SYSTEM_BLOCKED:
      case DeviceState.PERMISSION_DENIED:
        instructions +=
          `1. Open Android Settings\n` +
          `2. Go to Apps or Application Manager\n` +
          `3. Find your browser (Chrome, Firefox, etc.)\n` +
          `4. Tap Permissions\n` +
          `5. Turn on ${device} permission\n` +
          `6. Return to this page and refresh`;
        break;
      default:
        instructions +=
          `1. Check that your ${device.toLowerCase()} is connected and working\n` +
          `2. Open Android Settings > Apps > [Browser] > Permissions\n` +
          `3. Enable ${device} permission\n` +
          `4. Close other apps that might be using your ${device.toLowerCase()}\n` +
          `5. Refresh this page`;
    }
  } else {
    // iOS
    switch (errorState) {
      case DeviceState.SYSTEM_BLOCKED:
      case DeviceState.PERMISSION_DENIED:
        instructions +=
          `1. Open iOS Settings\n` +
          `2. Scroll down and find your browser (Safari, Chrome, etc.)\n` +
          `3. Tap on the browser\n` +
          `4. Turn on ${device} permission\n` +
          `5. Return to this page and refresh`;
        break;
      default:
        instructions +=
          `1. Check that your ${device.toLowerCase()} is working in other apps\n` +
          `2. Open iOS Settings > [Browser] > ${device}\n` +
          `3. Enable ${device} access\n` +
          `4. Close other apps using your ${device.toLowerCase()}\n` +
          `5. Refresh this page`;
    }
  }

  console.log(`📱 Showing ${platform} instructions for ${deviceType}`);
  alert(instructions);
};

// Cross-platform instructions for unknown platforms
const showCrossPlatformInstructions = (deviceType: 'camera' | 'microphone', errorState: DeviceState) => {
  const device = deviceType === 'camera' ? 'Camera' : 'Microphone';

  let instructions = `To fix your ${device}:\n\n`;

  switch (errorState) {
    case DeviceState.SYSTEM_BLOCKED:
      instructions +=
        `1. Open your device's Settings or System Preferences\n` +
        `2. Look for Privacy, Security, or Permissions section\n` +
        `3. Find ${device} settings\n` +
        `4. Allow access for your browser\n` +
        `5. Refresh this page`;
      break;

    case DeviceState.PERMISSION_DENIED:
      instructions +=
        `1. Look for the ${device.toLowerCase()} icon in your browser's address bar\n` +
        `2. Click it and select "Allow"\n` +
        `3. Or check your browser settings > Privacy > ${device}\n` +
        `4. Refresh this page`;
      break;

    default:
      instructions +=
        `1. Check your ${device.toLowerCase()} is properly connected\n` +
        `2. Verify device and browser permissions\n` +
        `3. Close other apps that might be using your ${device.toLowerCase()}\n` +
        `4. Refresh this page`;
  }

  console.log(`🌐 Showing cross-platform instructions for ${deviceType}`);
  alert(instructions);
};

// Enhanced system instructions with browser-specific guidance
export const showSystemInstructionsForError = (
  os: string,
  deviceType: string,
  errorState: DeviceState,
  browser: string = 'Browser'
) => {
  const device = deviceType === 'camera' ? 'Camera' : 'Microphone';
  let instructions = '';

  if (os === 'Windows') {
    switch (errorState) {
      case DeviceState.SYSTEM_BLOCKED:
        instructions =
          `Windows 10/11 - Unmute your ${device}:\n\n` +
          `1. Press Windows key + I to open Settings\n` +
          `2. Go to System > Sound (for microphone) or Privacy & security > Camera\n` +
          `3. Under "Input", check your ${device.toLowerCase()} volume\n` +
          `4. Click "Device properties" and ensure it's not muted\n` +
          `5. In Privacy settings, turn on "${device} access for this device"\n` +
          `6. Allow ${browser} to access your ${device.toLowerCase()}\n` +
          `7. Refresh this page`;
        break;

      case DeviceState.PERMISSION_DENIED:
        instructions =
          `Windows 10/11 - Allow ${device} Access:\n\n` +
          `1. Press Windows key + I to open Settings\n` +
          `2. Go to Privacy & security\n` +
          `3. Click "${device}" in the left sidebar\n` +
          `4. Turn on "${device} access for this device"\n` +
          `5. Turn on "Let apps access your ${device.toLowerCase()}"\n` +
          `6. Make sure ${browser} is in the allowed list\n` +
          `7. Refresh this page`;
        break;

      case DeviceState.NO_DEVICE:
      case DeviceState.DEVICE_UNPLUGGED:
        instructions =
          `Windows 10/11 - Fix ${device} Connection:\n\n` +
          `1. Check your ${device.toLowerCase()} is properly connected\n` +
          `2. Press Windows key + X, select Device Manager\n` +
          `3. Look under "Audio inputs" or "Cameras"\n` +
          `4. If you see a yellow warning, right-click and "Update driver"\n` +
          `5. Try unplugging and reconnecting your ${device.toLowerCase()}\n` +
          `6. Check Windows Settings > System > Sound to see available devices\n` +
          `7. Refresh this page`;
        break;

      case DeviceState.DEVICE_IN_USE:
        instructions =
          `Windows 10/11 - Free up your ${device}:\n\n` +
          `1. Close other apps that might be using your ${device.toLowerCase()}\n` +
          `2. Press Ctrl + Shift + Esc to open Task Manager\n` +
          `3. Look for apps using audio/video (Teams, Zoom, Skype, etc.)\n` +
          `4. End those tasks if not needed\n` +
          `5. Go to Privacy & security > ${device} to see which apps accessed it recently\n` +
          `6. Refresh this page`;
        break;

      default:
        instructions =
          `Windows 10/11 - Fix your ${device}:\n\n` +
          `1. Press Windows key + I to open Settings\n` +
          `2. Go to Privacy & security > ${device}\n` +
          `3. Turn on ${device.toLowerCase()} access and allow ${browser}\n` +
          `4. Check System > Sound for volume and device settings\n` +
          `5. Refresh this page`;
    }
  } else if (os === 'Mac') {
    const macVersion = navigator.userAgent.includes('Intel') ? 'macOS' : 'macOS (Apple Silicon)';

    switch (errorState) {
      case DeviceState.SYSTEM_BLOCKED:
        instructions =
          `${macVersion} - Unmute your ${device}:\n\n` +
          `1. Click Apple menu > System Preferences (or System Settings on macOS 13+)\n` +
          `2. Click "Security & Privacy" (or "Privacy & Security")\n` +
          `3. Click the "Privacy" tab\n` +
          `4. Select "${device}" from the list\n` +
          `5. Check the box next to ${browser}\n` +
          `6. If prompted, enter your admin password\n` +
          `7. Restart ${browser} and refresh this page`;
        break;

      case DeviceState.PERMISSION_DENIED:
        instructions =
          `${macVersion} - Allow ${device} Access:\n\n` +
          `1. Click Apple menu > System Preferences\n` +
          `2. Click "Security & Privacy"\n` +
          `3. Click the "Privacy" tab (you may need to click the lock to make changes)\n` +
          `4. Select "${device}" from the list on the left\n` +
          `5. Check the box next to "${browser}"\n` +
          `6. Close System Preferences\n` +
          `7. Quit and restart ${browser}, then refresh this page`;
        break;

      case DeviceState.DEVICE_IN_USE:
        instructions =
          `${macVersion} - Free up your ${device}:\n\n` +
          `1. Press Cmd + Space, type "Activity Monitor" and press Enter\n` +
          `2. Search for apps using ${device.toLowerCase()} (look for microphone/camera icons)\n` +
          `3. Select those apps and click "Force Quit" if needed\n` +
          `4. Check which apps recently accessed ${device.toLowerCase()} in Privacy settings\n` +
          `5. Refresh this page`;
        break;

      case DeviceState.NO_DEVICE:
      case DeviceState.DEVICE_UNPLUGGED:
        instructions =
          `${macVersion} - Fix ${device} Connection:\n\n` +
          `1. Check your ${device.toLowerCase()} is properly connected\n` +
          `2. Click Apple menu > About This Mac > System Report\n` +
          `3. Under Hardware, click "USB" or "Camera" to see connected devices\n` +
          `4. Try different USB ports or restart your Mac\n` +
          `5. Test your ${device.toLowerCase()} in other apps (FaceTime, Photo Booth)\n` +
          `6. Refresh this page`;
        break;

      default:
        instructions =
          `${macVersion} - Fix your ${device}:\n\n` +
          `1. Open System Preferences > Security & Privacy\n` +
          `2. Go to Privacy tab > ${device}\n` +
          `3. Make sure ${browser} is checked\n` +
          `4. Restart ${browser} and refresh this page`;
    }
  } else if (os === 'Linux') {
    switch (errorState) {
      case DeviceState.SYSTEM_BLOCKED:
        instructions =
          `Linux - Unmute your ${device}:\n\n` +
          `1. Open your system settings or sound preferences\n` +
          `2. Check input volume is not muted (look for microphone icon)\n` +
          `3. Try: pulseaudio --kill && pulseaudio --start\n` +
          `4. Or: sudo alsamixer (press 'M' to unmute if needed)\n` +
          `5. Check user permissions: groups $USER\n` +
          `6. Refresh this page`;
        break;

      case DeviceState.PERMISSION_DENIED:
        instructions =
          `Linux - Allow ${device} Access:\n\n` +
          `1. Add user to audio/video groups:\n` +
          `   sudo usermod -a -G audio,video $USER\n` +
          `2. Check device permissions:\n` +
          `   ls -l /dev/video* /dev/snd/*\n` +
          `3. Log out and back in for group changes to take effect\n` +
          `4. Refresh this page`;
        break;

      case DeviceState.NO_DEVICE:
      case DeviceState.DEVICE_UNPLUGGED:
        instructions =
          `Linux - Fix ${device} Connection:\n\n` +
          `1. Check device is connected: lsusb\n` +
          `2. Check video devices: ls /dev/video*\n` +
          `3. Check audio devices: arecord -l\n` +
          `4. Install drivers if needed: sudo apt install linux-modules-extra-$(uname -r)\n` +
          `5. Try: sudo modprobe uvcvideo (for cameras)\n` +
          `6. Refresh this page`;
        break;

      case DeviceState.DEVICE_IN_USE:
        instructions =
          `Linux - Free up your ${device}:\n\n` +
          `1. Find processes using device:\n` +
          `   lsof /dev/video* /dev/snd/* 2>/dev/null\n` +
          `2. Kill those processes:\n` +
          `   sudo killall [process-name]\n` +
          `3. Restart ${browser}\n` +
          `4. Refresh this page`;
        break;

      default:
        instructions =
          `Linux - Fix your ${device}:\n\n` +
          `1. Check device is connected: lsusb\n` +
          `2. Check permissions: ls -l /dev/video* /dev/snd/*\n` +
          `3. Add user to groups: sudo usermod -a -G audio,video $USER\n` +
          `4. Install missing drivers if needed\n` +
          `5. Check PulseAudio/ALSA settings\n` +
          `6. Refresh this page`;
    }
  }

  console.log(`📋 Showing ${os} instructions for ${deviceType} error: ${errorState}`);

  // Create a more user-friendly modal instead of alert
  showInstructionsModal(instructions, `${os} ${device} Setup`);
};

// Create a better instructions modal
const showInstructionsModal = (instructions: string, title: string) => {
  // Create modal elements
  const modal = document.createElement('div');
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10000;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  `;

  const content = document.createElement('div');
  content.style.cssText = `
    background: white;
    border-radius: 12px;
    padding: 24px;
    max-width: 500px;
    max-height: 80vh;
    overflow-y: auto;
    margin: 20px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.24);
  `;

  const titleEl = document.createElement('h3');
  titleEl.textContent = title;
  titleEl.style.cssText = `
    margin: 0 0 16px 0;
    color: #202124;
    font-size: 20px;
    font-weight: 500;
  `;

  const instructionsEl = document.createElement('pre');
  instructionsEl.textContent = instructions;
  instructionsEl.style.cssText = `
    white-space: pre-wrap;
    font-family: inherit;
    line-height: 1.5;
    color: #5f6368;
    margin: 0 0 20px 0;
    font-size: 14px;
  `;

  const button = document.createElement('button');
  button.textContent = 'Got it';
  button.style.cssText = `
    background: #1a73e8;
    color: white;
    border: none;
    border-radius: 8px;
    padding: 10px 24px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    float: right;
  `;

  button.onclick = () => document.body.removeChild(modal);
  modal.onclick = e => {
    if (e.target === modal) document.body.removeChild(modal);
  };

  content.appendChild(titleEl);
  content.appendChild(instructionsEl);
  content.appendChild(button);
  modal.appendChild(content);
  document.body.appendChild(modal);
};

// Enhanced browser instructions with error context
export const showBrowserInstructionsForError = (browser: string, deviceType: string, errorState: DeviceState) => {
  const device = deviceType === 'camera' ? 'Camera' : 'Microphone';
  let contextPrefix = '';

  switch (errorState) {
    case DeviceState.PERMISSION_DENIED:
      contextPrefix = `${browser} has blocked ${device.toLowerCase()} access.\n\n`;
      break;
    case DeviceState.SYSTEM_BLOCKED:
      contextPrefix = `Your ${device.toLowerCase()} might be blocked by ${browser} security.\n\n`;
      break;
    default:
      contextPrefix = `To enable ${device.toLowerCase()} access in ${browser}:\n\n`;
  }

  const instructions = getSettingsInstructions(browser, deviceType);
  console.log(`🌐 Showing ${browser} instructions for ${deviceType} error: ${errorState}`);

  showInstructionsModal(contextPrefix + instructions, `${browser} ${device} Settings`);
};

// Enhanced browser-specific instructions
const getSettingsInstructions = (browser: string, deviceType: string): string => {
  const device = deviceType === 'camera' ? 'Camera' : 'Microphone';

  switch (browser) {
    case 'Chrome':
      return (
        `1. Look for the ${device.toLowerCase()} icon in your address bar (next to the URL)\n` +
        `2. Click the icon and select "Allow" for this site\n` +
        `3. If no icon appears, click the lock icon and check site permissions\n` +
        `4. Refresh the page\n\n` +
        `Alternative: Go to chrome://settings/content/${deviceType.toLowerCase()}`
      );

    case 'Firefox':
      return (
        `1. Look for the ${device.toLowerCase()} icon in your address bar (usually on the left)\n` +
        `2. Click the icon and select "Allow" for this site\n` +
        `3. Choose "Remember this decision" if available\n` +
        `4. If no icon appears, click the shield icon or site information icon\n` +
        `5. Refresh the page\n\n` +
        `Alternative: Firefox Menu > Settings > Privacy & Security > Permissions > ${device}`
      );

    case 'Safari':
      return (
        `1. Click "Safari" in the menu bar, then "Settings for This Website"\n` +
        `2. Change ${device} setting to "Allow"\n` +
        `3. Refresh the page\n\n` +
        `Alternative:\n` +
        `1. Safari > Preferences > Websites\n` +
        `2. Click "${device}" in the sidebar\n` +
        `3. Set this website to "Allow"`
      );

    case 'Edge':
      return (
        `1. Look for the ${device.toLowerCase()} icon in your address bar\n` +
        `2. Click the icon and select "Allow" for this site\n` +
        `3. If no icon appears, click the lock icon to manage permissions\n` +
        `4. Refresh the page\n\n` +
        `Alternative: Go to edge://settings/content/${deviceType.toLowerCase()}`
      );

    case 'Opera':
      return (
        `1. Look for the ${device.toLowerCase()} icon in your address bar\n` +
        `2. Click it and select "Allow" for this site\n` +
        `3. Or click the lock icon to manage site permissions\n` +
        `4. Refresh the page\n\n` +
        `Alternative: Settings > Advanced > Privacy & Security > Site Settings`
      );

    case 'Brave':
      return (
        `1. Click the Brave shield icon in your address bar\n` +
        `2. Look for ${device} controls and set to "Allow"\n` +
        `3. Or click the lock icon to manage permissions\n` +
        `4. Refresh the page\n\n` +
        `Alternative: Settings > Shields > Advanced Controls`
      );

    case 'Vivaldi':
      return (
        `1. Look for the ${device.toLowerCase()} icon in your address bar\n` +
        `2. Click it and select "Allow" for this site\n` +
        `3. Or go to page settings via the address bar\n` +
        `4. Refresh the page\n\n` +
        `Alternative: Settings > Privacy > Site Settings`
      );

    default:
      return (
        `1. Look for a ${device.toLowerCase()} or lock icon in your address bar\n` +
        `2. Click it and look for camera/microphone permissions\n` +
        `3. Set permissions to "Allow" for this website\n` +
        `4. Refresh the page\n\n` +
        `Check your browser's privacy or security settings for more options`
      );
  }
};

// Backward compatibility functions
export const openWindowsSettings = (deviceType: 'camera' | 'microphone') => {
  openWindowsSettingsForError(deviceType, DeviceState.SYSTEM_BLOCKED);
};

export const openDeviceSettings = (deviceType: 'camera' | 'microphone') => {
  openWindowsSettingsForError(deviceType, DeviceState.SYSTEM_BLOCKED);
};

export const showBrowserInstructions = (browser: string, deviceType: string) => {
  showBrowserInstructionsForError(browser, deviceType, DeviceState.PERMISSION_DENIED);
};
